<!--
  AuditLogTable.svelte — Reusable audit log table component.

  Displays a paginated table of audit log entries with resolved usernames,
  action badges with colour-coding, relative timestamps, and detail tooltips.

  Usage:
    <AuditLogTable
      logs={auditLogs}
      users={usersMap}
      total={totalCount}
      {limit}
      {page}
      {loading}
      {sortBy}
      {sortDir}
      onpagechange={handlePageChange}
      onsortchange={handleSortChange}
    />

  Sorting is performed server-side: this component only reports the requested
  key/direction back to the parent, which re-fetches the page.
-->
<script lang="ts">
	import {
		Badge,
		Button,
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell
	} from 'flowbite-svelte';
	import UserBadge from './UserBadge.svelte';
	import SortableTableHeadCell from './SortableTableHeadCell.svelte';
	import { SvelteSet } from 'svelte/reactivity';
	import type { AuditLog, AuditUserRef, SortDirection } from '../AppTypes';
	import { t, t2 } from '../i18n';

	/** Badge colour union accepted by flowbite-svelte Badge. */
	type BadgeColor = 'red' | 'yellow' | 'green' | 'indigo' | 'purple' | 'pink' | 'blue' | 'gray' | 'primary';

	/** Set of log IDs whose detail rows are currently expanded. */
	let expandedRows = new SvelteSet<number>();

	/** Toggle expansion of a single log row. */
	function toggleRow(id: number): void {
		if (expandedRows.has(id)) {
			expandedRows.delete(id);
		} else {
			expandedRows.add(id);
		}
	}

	interface Props {
		/** The audit log entries to display. */
		logs?: AuditLog[];
		/** Map of user IDs to resolved names, provided by the API. */
		users?: Record<number, AuditUserRef>;
		/** Total number of matching records (for pagination). */
		total?: number;
		/** Page size. */
		limit?: number;
		/** Current 1-based page number. */
		page?: number;
		/** Whether a load is in progress. */
		loading?: boolean;
		/** Whether to show the Details column. */
		showDetails?: boolean;
		/** Active server-side sort key (`''` = server default, newest first). */
		sortBy?: string;
		/** Active server-side sort direction. */
		sortDir?: SortDirection;
		/** Callback fired with the new page number when pagination changes. */
		onpagechange?: (_page: number) => void;
		/** Callback fired with the requested sort key and direction. Omit to disable sorting. */
		onsortchange?: (_key: string, _dir: SortDirection) => void;
	}

	let {
		logs = [],
		users = {},
		total = 0,
		limit = 25,
		page = 1,
		loading = false,
		showDetails = true,
		sortBy = '',
		sortDir = 'desc',
		onpagechange,
		onsortchange
	}: Props = $props();

	/** Forward a header click to the parent, which owns the fetch. */
	function handleSort(key: string, dir: SortDirection): void {
		onsortchange?.(key, dir);
	}

	// ── Action display maps ───────────────────────────────────

	/** Colour for each audit action type. */
	const actionColors: Record<string, BadgeColor> = {
		USER_REGISTERED: 'green',
		USER_LOGIN: 'blue',
		USER_UPDATED: 'indigo',
		USER_ACTIVATED: 'green',
		USER_DEACTIVATED: 'red',
		USER_DELETED: 'red',
		USER_VERIFIED: 'green',
		PASSWORD_CHANGED: 'yellow',
		PASSWORD_RESET_REQUESTED: 'yellow',
		PASSWORD_RESET_COMPLETED: 'yellow',
		EMAIL_OTP_REQUESTED: 'blue',
		TWO_FACTOR_ENABLED: 'green',
		TWO_FACTOR_DISABLED: 'red',
		ROLE_CHANGED: 'purple',
		ROLE_CREATED: 'purple',
		ROLE_UPDATED: 'purple',
		ROLE_DELETED: 'red',
		SETTINGS_CHANGED: 'pink',
		APPLICATION_CREATED: 'indigo',
		APPLICATION_UPDATED: 'indigo',
		APPLICATION_DELETED: 'red',
		APPLICATION_USER_REGISTERED: 'green',
		APPLICATION_USER_UNREGISTERED: 'red',
		APPLICATION_PERMISSION_CHANGED: 'purple',
		EMAIL_TEMPLATE_CREATED: 'green',
		EMAIL_TEMPLATE_UPDATED: 'indigo',
		EMAIL_TEMPLATE_DELETED: 'red',
		EMAIL_CHANGE_REQUESTED: 'yellow',
		EMAIL_CHANGED: 'green',
		SOCIAL_ACCOUNT_LINKED: 'indigo',
		LICENSE_SEATS_UPDATED: 'indigo',
		LICENSE_ACTIVATION_REVOKED: 'yellow',
		SUPPORT_TICKET_CREATED: 'green',
		SUPPORT_TICKET_UPDATED: 'blue',
		SUPPORT_CONFIG_CHANGED: 'purple',
		TOKEN_TRANSACTION: 'blue',
		NEWSLETTER_CREATED: 'green',
		NEWSLETTER_UPDATED: 'indigo',
		NEWSLETTER_DELETED: 'red',
		NEWSLETTER_STARTED: 'blue',
		NEWSLETTER_PAUSED: 'yellow',
		NEWSLETTER_RESUMED: 'green',
		NEWSLETTER_SENT: 'blue',
		NEWSLETTER_CANCELLED: 'gray',
		NEWSLETTER_SUBSCRIBED: 'green',
		NEWSLETTER_UNSUBSCRIBED: 'yellow',
		SYSTEM_ERROR: 'red'
	};

	/** FontAwesome icon for each audit action type. */
	const actionIcons: Record<string, string> = {
		USER_REGISTERED: 'fa-user-plus',
		USER_LOGIN: 'fa-right-to-bracket',
		USER_UPDATED: 'fa-user-pen',
		USER_ACTIVATED: 'fa-user-check',
		USER_DEACTIVATED: 'fa-user-slash',
		USER_DELETED: 'fa-user-xmark',
		USER_VERIFIED: 'fa-circle-check',
		PASSWORD_CHANGED: 'fa-key',
		PASSWORD_RESET_REQUESTED: 'fa-envelope',
		PASSWORD_RESET_COMPLETED: 'fa-lock-open',
		EMAIL_OTP_REQUESTED: 'fa-envelope-open-text',
		TWO_FACTOR_ENABLED: 'fa-shield-halved',
		TWO_FACTOR_DISABLED: 'fa-shield',
		ROLE_CHANGED: 'fa-user-shield',
		ROLE_CREATED: 'fa-plus-circle',
		ROLE_UPDATED: 'fa-pen-to-square',
		ROLE_DELETED: 'fa-trash',
		SETTINGS_CHANGED: 'fa-gear',
		APPLICATION_CREATED: 'fa-cube',
		APPLICATION_UPDATED: 'fa-cubes',
		APPLICATION_DELETED: 'fa-cube',
		APPLICATION_USER_REGISTERED: 'fa-user-plus',
		APPLICATION_USER_UNREGISTERED: 'fa-user-minus',
		APPLICATION_PERMISSION_CHANGED: 'fa-key',
		EMAIL_TEMPLATE_CREATED: 'fa-plus-circle',
		EMAIL_TEMPLATE_UPDATED: 'fa-pen-to-square',
		EMAIL_TEMPLATE_DELETED: 'fa-trash',
		EMAIL_CHANGE_REQUESTED: 'fa-envelope',
		EMAIL_CHANGED: 'fa-envelope-circle-check',
		SOCIAL_ACCOUNT_LINKED: 'fa-link',
		LICENSE_SEATS_UPDATED: 'fa-user-group',
		LICENSE_ACTIVATION_REVOKED: 'fa-desktop',
		SUPPORT_TICKET_CREATED: 'fa-life-ring',
		SUPPORT_TICKET_UPDATED: 'fa-headset',
		SUPPORT_CONFIG_CHANGED: 'fa-sliders',
		TOKEN_TRANSACTION: 'fa-coins',
		NEWSLETTER_CREATED: 'fa-plus-circle',
		NEWSLETTER_UPDATED: 'fa-pen-to-square',
		NEWSLETTER_DELETED: 'fa-trash',
		NEWSLETTER_STARTED: 'fa-play',
		NEWSLETTER_PAUSED: 'fa-pause',
		NEWSLETTER_RESUMED: 'fa-forward',
		NEWSLETTER_SENT: 'fa-paper-plane',
		NEWSLETTER_CANCELLED: 'fa-ban',
		NEWSLETTER_SUBSCRIBED: 'fa-envelope-circle-check',
		NEWSLETTER_UNSUBSCRIBED: 'fa-envelope-open',
		SYSTEM_ERROR: 'fa-triangle-exclamation'
	};

	// ── Helpers ───────────────────────────────────────────────

	/** Format a date string for display. */
	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleString();
	}

	/** Relative time label (e.g. "5m ago"). */
	function formatRelativeTime(dateString: string): string {
		const diff = Date.now() - new Date(dateString).getTime();
		const minutes = Math.floor(diff / 60000);
		const hours = Math.floor(diff / 3600000);
		const days = Math.floor(diff / 86400000);
		/** Return "just now" for very recent events. */
		if (minutes < 1) return t2('just now');
		/** Return minutes-based label for events under an hour old. */
		if (minutes < 60) return t2('{{count}}m ago', { count: minutes });
		/** Return hours-based label for events under a day old. */
		if (hours < 24) return t2('{{count}}h ago', { count: hours });
		/** Return days-based label for events under a week old. */
		if (days < 7) return t2('{{count}}d ago', { count: days });
		return new Date(dateString).toLocaleDateString();
	}

	/** Safely parse details JSON. */
	function parseDetails(details: unknown): unknown {
		if (!details) return null;
		try {
			return typeof details === 'string' ? JSON.parse(details) : details;
		} catch {
			return details;
		}
	}

	let totalPages = $derived(Math.ceil(total / limit));
</script>

{#if loading}
	<div class="p-8 text-center">
		<i class="fa-solid fa-spinner fa-spin mb-2 text-2xl text-gray-400"></i>
		<p class="text-gray-500 dark:text-gray-400">{$t("Loading audit logs…")}</p>
	</div>
{:else if logs.length === 0}
	<div class="p-8 text-center">
		<i class="fa-solid fa-clipboard-list mb-4 text-4xl text-gray-300 dark:text-gray-600"></i>
		<p class="text-gray-500 dark:text-gray-400">{$t("No audit logs found.")}</p>
	</div>
{:else}
	<Table divClass="widget !p-1 relative" class="dataTable">
		<TableHead>
			{#if onsortchange}
				<SortableTableHeadCell
					key="created"
					{sortBy}
					{sortDir}
					defaultDir="desc"
					class="w-36"
					onsort={handleSort}>{$t("Time")}</SortableTableHeadCell
				>
				<SortableTableHeadCell key="action" {sortBy} {sortDir} class="w-40" onsort={handleSort}>
					{$t("Action")}
				</SortableTableHeadCell>
			{:else}
				<TableHeadCell class="w-36">{$t("Time")}</TableHeadCell>
				<TableHeadCell class="w-40">{$t("Action")}</TableHeadCell>
			{/if}
			<TableHeadCell class="w-32">{$t("Performed By")}</TableHeadCell>
			<TableHeadCell class="w-32">{$t("Target")}</TableHeadCell>
			{#if showDetails}
				<TableHeadCell>{$t("Details")}</TableHeadCell>
			{/if}
		</TableHead>
		<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
			{#each logs as log (log.id)}
				{@const parsed = parseDetails(log.details)}
				{@const hasDetails = !!parsed}
				{@const isExpanded = expandedRows.has(log.id)}
				<TableBodyRow
					class={hasDetails ? 'cursor-pointer' : ''}
					onclick={() => hasDetails && toggleRow(log.id)}
				>
					<TableBodyCell>
						<div class="flex items-center gap-2">
							{#if hasDetails}
								<i
									class="fa-solid fa-chevron-right text-xs text-gray-400 transition-transform duration-150 {isExpanded ? 'rotate-90' : ''}"
								></i>
							{:else}
								<span class="inline-block w-3"></span>
							{/if}
							<div class="flex flex-col">
								<span class="text-sm font-medium text-gray-900 dark:text-white">
									{formatRelativeTime(log.created)}
								</span>
								<span class="text-xs text-gray-500 dark:text-gray-400">
									{formatDate(log.created)}
								</span>
							</div>
						</div>
					</TableBodyCell>
					<TableBodyCell>
						<Badge color={actionColors[log.action] || 'gray'}>
							<i class="fa-solid {actionIcons[log.action] || 'fa-circle'} me-1"></i>
							{log.action.replace(/_/g, ' ')}
						</Badge>
					</TableBodyCell>
					<TableBodyCell>
						{#if log.userId && users[log.userId]}
							<UserBadge
								userId={log.userId}
								firstName={users[log.userId].firstName}
								lastName={users[log.userId].lastName}
								email={users[log.userId].email}
							/>
						{:else if log.userId}
							<span class="text-sm text-gray-400">{$t("User #{{id}}", { id: log.userId })}</span>
						{:else}
							<span class="text-sm text-gray-400">—</span>
						{/if}
					</TableBodyCell>
					<TableBodyCell>
						{#if log.targetUserId && users[log.targetUserId]}
							<UserBadge
								userId={log.targetUserId}
								firstName={users[log.targetUserId].firstName}
								lastName={users[log.targetUserId].lastName}
								email={users[log.targetUserId].email}
							/>
						{:else if log.targetUserId}
							<span class="text-sm text-gray-400">{$t("User #{{id}}", { id: log.targetUserId })}</span>
						{:else}
							<span class="text-sm text-gray-400">—</span>
						{/if}
					</TableBodyCell>
					{#if showDetails}
						<TableBodyCell>
							{#if hasDetails}
								<code
									class="block max-w-[200px] truncate rounded bg-gray-100 px-2 py-1 text-xs dark:bg-gray-800"
								>
									{typeof parsed === 'object' ? JSON.stringify(parsed) : parsed}
								</code>
							{:else}
								<span class="text-sm text-gray-400">—</span>
							{/if}
						</TableBodyCell>
					{/if}
				</TableBodyRow>
				<!-- Expanded detail row -->
				{#if isExpanded && hasDetails}
					<TableBodyRow class="!bg-gray-50 dark:!bg-gray-800/50">
						<TableBodyCell colspan={showDetails ? 5 : 4} class="px-6 py-4">
							<div
								class="grid gap-x-4 gap-y-1"
								style="grid-template-columns: max-content 1fr;"
							>
								{#if typeof parsed === 'object' && parsed !== null}
									{#each Object.entries(parsed as Record<string, unknown>) as [key, value] (key)}
										<span
											class="self-start py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
										>
											{key}
										</span>
										{#if key === 'stack' && typeof value === 'string'}
											<pre
												class="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-gray-100 p-2 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300"
											>{value}</pre>
										{:else}
											<span class="self-center text-sm text-gray-800 dark:text-gray-200">
												{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '—')}
											</span>
										{/if}
									{/each}
								{:else}
									<pre
										class="col-span-2 whitespace-pre-wrap rounded bg-gray-100 p-2 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300"
									>{typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2)}</pre>
								{/if}
								{#if log.ipAddress}
									<span
										class="self-center py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
									>
										{$t("IP Address")}
									</span>
									<span class="self-center text-sm text-gray-800 dark:text-gray-200">
										{log.ipAddress}
									</span>
								{/if}
							</div>
						</TableBodyCell>
					</TableBodyRow>
				{/if}
			{/each}
		</TableBody>
	</Table>

	<!-- Pagination -->
	{#if totalPages > 1}
		<div class="flex items-center justify-between border-t border-gray-200 p-4 dark:border-gray-700">
			<p class="text-sm text-gray-500 dark:text-gray-400">
				{$t("Showing {{from}} to {{to}} of {{total}} entries", { from: (page - 1) * limit + 1, to: Math.min(page * limit, total), total })}
			</p>
			<div class="flex items-center gap-2">
				<Button
					color="alternative"
					size="sm"
					disabled={page <= 1}
					onclick={() => onpagechange?.(page - 1)}
				>
					<i class="fa-solid fa-chevron-left me-1"></i>
					{$t("Previous")}
				</Button>
				<span class="px-3 text-sm text-gray-600 dark:text-gray-400">
					{$t("Page {{page}} of {{totalPages}}", { page, totalPages })}
				</span>
				<Button
					color="alternative"
					size="sm"
					disabled={page >= totalPages}
					onclick={() => onpagechange?.(page + 1)}
				>
					{$t("Next")}
					<i class="fa-solid fa-chevron-right ms-1"></i>
				</Button>
			</div>
		</div>
	{/if}
{/if}
